import { BaseService } from '@/chassis/data/services/base-service';
import { BaseFormService, ValidationRules } from '@/chassis/data/services/base-form-service';
import { createSimpleNormalizer } from '@/chassis/data/services/normalizer-utils';
import { http } from '@/chassis/data/services/http-client';

export interface CitizenRecord {
    id: string;
    userId?: string;
    name: string;
    birthdate: string;
    email?: string;
    phone?: string;
    address?: string;
    zipCode?: string;
    // Representante legal (Pato Branco / LCPE, 21/09/2026): RG e endereco estruturado. Opcionais.
    identityNumber?: string;
    identityIssuer?: string;
    addressNumber?: string;
    addressComplement?: string;
    addressDistrict?: string;
    city?: string;
    state?: string;
    cityCode?: string;
    country?: string;
    citizenshipStatus?: 'ACTIVE' | 'SUSPENDED' | 'VERIFIED' | 'PENDING' | '';
    cpf: string;
    isVerified: boolean;
    active?: boolean;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export interface CitizenFilters {
    userId?: string;
    searchTerm?: string;
    searchText?: string;
    cpf?: string;
    citizenshipStatus?: string;
    active?: string;
    page?: number;
    limit?: number;
}

export interface CreateCitizenDto {
    userId: string;
    name: string;
    birthdate: string;
    email?: string;
    phone?: string;
    address?: string;
    zipCode?: string;
    // Representante legal (Pato Branco / LCPE, 21/09/2026): RG e endereco estruturado. Opcionais.
    identityNumber?: string;
    identityIssuer?: string;
    addressNumber?: string;
    addressComplement?: string;
    addressDistrict?: string;
    city?: string;
    state?: string;
    cityCode?: string;
    country?: string;
    citizenshipStatus?: 'ACTIVE' | 'SUSPENDED' | 'VERIFIED' | 'PENDING' | '';
    cpf: string;
    isVerified?: boolean;
    active?: boolean;
}

export type UpdateCitizenDto = Partial<CreateCitizenDto>;

export type CitizenFormData = Omit<CitizenRecord, 'id'> & { active?: boolean; id?: string; recordId?: string };
export type CitizenFormState = CitizenFormData;

const resolveActiveFlag = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return true;
};

const baseNormalizeCitizenRecord = createSimpleNormalizer<CitizenRecord>();

const normalizeCitizenRecord = (record: CitizenRecord): CitizenRecord => {
    const normalized = baseNormalizeCitizenRecord(record);
    const active = resolveActiveFlag(normalized.active ?? normalized.active);
    return {
        ...normalized,
        active,
    };
};

// --- Public registration types (used by Register form) ---

export interface CheckIdentifiersPayload {
    cpf?: string;
    email?: string;
}

export interface IdentifierConflicts {
    cpfExists: boolean;
    emailExists: boolean;
}

export interface SendVerificationEmailPayload {
    email: string;
    name?: string;
}

export interface VerificationEmailResponse {
    communicationId: string;
    expiresAt?: string;
}

export interface VerifyEmailCodePayload {
    email: string;
    validationCode: string;
    communicationId?: string;
}

export interface VerifyEmailCodeResponse {
    communicationId: string;
    verifiedAt: string;
}

export interface RegisterCitizenPayload {
    pais: string;
    name: string;
    cpf: string;
    birthdate: string;
    email: string;
    password: string;
    verificationCommunicationId: string;
}

class CitizensService extends BaseService<CitizenRecord, CreateCitizenDto, UpdateCitizenDto, CitizenFilters> {
    constructor() {
        super({
            basePath: '/citizens',
            normalizeRecord: normalizeCitizenRecord,
        });
    }

    /** Check if CPF/email are already registered (public endpoint, no auth). */
    async checkIdentifiers(payload: CheckIdentifiersPayload): Promise<IdentifierConflicts> {
        return http.post<IdentifierConflicts>('/public/citizens/check-identifiers', payload, {
            allowLegacyPath: true,
        });
    }

    /** Send a verification email with a code for registration (public endpoint). */
    async sendVerificationEmail(payload: SendVerificationEmailPayload): Promise<VerificationEmailResponse> {
        return http.post<VerificationEmailResponse>(
            '/public/citizens/notifications/registration-step-email',
            payload,
            { allowLegacyPath: true },
        );
    }

    /** Verify the email code sent during registration (public endpoint). */
    async verifyEmailCode(payload: VerifyEmailCodePayload): Promise<VerifyEmailCodeResponse> {
        return http.post<VerifyEmailCodeResponse>(
            '/public/citizens/notifications/registration-step-email/verify',
            payload,
            { allowLegacyPath: true },
        );
    }

    /** Register a new citizen (public endpoint). */
    async register(payload: RegisterCitizenPayload): Promise<void> {
        await http.post('/public/citizens', payload, {
            allowLegacyPath: true,
        });
    }
}

export const citizensService = new CitizensService();

export const citizenFormDefaults: CitizenFormData = {
    name: '',
    birthdate: '',
    email: '',
    phone: '',
    address: '',
    zipCode: '',
    identityNumber: '',
    identityIssuer: '',
    addressNumber: '',
    addressComplement: '',
    addressDistrict: '',
    city: '',
    state: '',
    cityCode: '',
    country: 'Brasil',
    citizenshipStatus: '',
    cpf: '',
    isVerified: false,
    active: true,
    createdAt: undefined,
    updatedAt: undefined,
    createdBy: undefined,
    createdByName: undefined,
    createdByUser: undefined,
    updatedBy: undefined,
    updatedByName: undefined,
    updatedByUser: undefined,
};

const normalizeBoolean = (value: unknown, defaultValue: boolean) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return defaultValue;
};

const toTrimmedString = (value?: string | null) => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizeCitizenPayload = (formData: Partial<CitizenFormData>): Partial<CreateCitizenDto> => {
    const {
        createdAt,
        updatedAt,
        createdBy,
        createdByName,
        createdByUser,
        updatedBy,
        updatedByName,
        updatedByUser,
        ...submittable
    } = formData;

    const payload: Partial<CreateCitizenDto> = {};

    // userId vem do FormIdLookup — preservar para evitar que attachUserIdIfMissing injete o admin logado
    const userId = toTrimmedString(submittable.userId);
    if (userId) payload.userId = userId;

    payload.name = (submittable.name ?? '').trim();
    payload.birthdate = (submittable.birthdate ?? '').trim();
    payload.cpf = (submittable.cpf ?? '').trim();

    const email = toTrimmedString(submittable.email);
    if (email) payload.email = email;

    const phone = toTrimmedString(submittable.phone);
    if (phone) payload.phone = phone;

    const address = toTrimmedString(submittable.address);
    if (address) payload.address = address;

    const zipCode = toTrimmedString(submittable.zipCode);
    if (zipCode) payload.zipCode = zipCode;

    // Campos opcionais do representante: mandados sempre que presentes no form (vazio limpa).
    for (const field of ['identityNumber', 'identityIssuer', 'addressNumber', 'addressComplement', 'addressDistrict', 'city', 'state', 'cityCode', 'country'] as const) {
        if (field in submittable) {
            payload[field] = toTrimmedString(submittable[field]) ?? '';
        }
    }

    const status = toTrimmedString(submittable.citizenshipStatus)?.toUpperCase();
    if (status) {
        payload.citizenshipStatus = status as CreateCitizenDto['citizenshipStatus'];
    }

    payload.isVerified = normalizeBoolean(submittable.isVerified, false);
    const active = normalizeBoolean(submittable.active ?? submittable.active, true);
    payload.active = active;
    payload.active = active;

    return payload;
};

export const createCitizensFormService = () =>
    new BaseFormService<CitizenRecord, CitizenFormData, CreateCitizenDto, UpdateCitizenDto, CitizenFilters>({
        basePath: '/citizens',
        initialData: citizenFormDefaults,
        normalizeRecord: normalizeCitizenRecord,
        validationRules: {
            name: [ValidationRules.required('Nome é obrigatório')],
            birthdate: [ValidationRules.required('Data de nascimento é obrigatória')],
            cpf: [ValidationRules.required('CPF é obrigatório')],
        },
        transformForCreate: (formData: CitizenFormData) => sanitizeCitizenPayload(formData) as CreateCitizenDto,
        transformForUpdate: (formData: Partial<CitizenFormData>) => sanitizeCitizenPayload(formData) as UpdateCitizenDto,
        transformToForm: (record) => ({
            ...citizenFormDefaults,
            userId: record.userId,
            name: record.name || '',
            birthdate: record.birthdate || '',
            email: record.email || '',
            phone: record.phone || '',
            address: record.address || '',
            zipCode: record.zipCode || '',
            identityNumber: record.identityNumber || '',
            identityIssuer: record.identityIssuer || '',
            addressNumber: record.addressNumber || '',
            addressComplement: record.addressComplement || '',
            addressDistrict: record.addressDistrict || '',
            city: record.city || '',
            state: record.state || '',
            cityCode: record.cityCode || '',
            country: record.country || 'Brasil',
            citizenshipStatus: record.citizenshipStatus || '',
            cpf: record.cpf || '',
            isVerified: record.isVerified ?? false,
            active: record.active ?? record.active ?? true,
            createdAt: record.createdAt,
            updatedAt: record.updatedAt,
            createdBy: record.createdBy,
            createdByName: record.createdByName,
            createdByUser: record.createdByUser,
            updatedBy: record.updatedBy,
            updatedByName: record.updatedByName,
            updatedByUser: record.updatedByUser,
        }),
    });
